import { publicSupabase } from '@/integrations/supabase/client';

// Define interfaces for nested category data structure
interface CategoryMapping {
  category: {
    id: string;
    name: string;
    slug: string;
    description?: string;
  };
}

// Direct access to Supabase for public blog content
// Public Blog Post Endpoints
export const getPublicBlogs = async (page = 1, pageSize = 10, categoryId?: string | null, sort = 'desc') => {
  try {
    //console.log('Fetching public blogs with Supabase public client');
    
    // Calculate pagination
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;
    
    // Start with a query for published blogs only
    let query = publicSupabase
      .from('blogs')
      .select(`
        *,
        categories:blog_category_mappings(
          category:blog_categories(id, name, slug, description)
        )
      `, { count: 'exact' })
      .eq('status', 'published')
      .order('created_at', { ascending: sort === 'asc' });
    
    // Add category filter if needed
    if (categoryId) {
      // For category filter, we need to join through the junction table
      query = query.filter('categories.category.id', 'eq', categoryId);
    }
    
    // Add pagination
    query = query.range(from, to);
    
    // Execute the query
    const { data, count, error } = await query;
    
    if (error) {
      //console.error('Error fetching blogs:', error);
      throw error;
    }
    
    // Transform the data structure to match the expected format
    const transformedData = data?.map(blog => {
      // Extract categories from the nested structure - handle null/undefined categories safely
      const categories = blog.categories && Array.isArray(blog.categories)
        ? blog.categories.map((item: CategoryMapping) => item.category).filter(Boolean) || []
        : [];
      return {
        ...blog,
        categories
      };
    });
    
    return {
      data: transformedData || [],
      pagination: {
        page,
        pageSize,
        totalItems: count || 0,
        totalPages: count ? Math.ceil(count / pageSize) : 0
      }
    };
  } catch (error) {
    //console.error('Error in getPublicBlogs:', error);
    throw error;
  }
};

export const getPublicBlogBySlug = async (slug: string) => {
  try {
    //console.log('Fetching public blog by slug with Supabase public client');
    
    // Query for a published blog with the given slug
    const { data, error } = await publicSupabase
      .from('blogs')
      .select(`
        *,
        categories:blog_category_mappings(
          category:blog_categories(id, name, slug, description)
        )
      `)
      .eq('status', 'published')
      .eq('slug', slug)
      .single();
    
    if (error) {
      //console.error('Error fetching blog by slug:', error);
      throw error;
    }
    
    // Transform the data structure to match the expected format
    if (data) {
      // Extract categories from the nested structure - handle null/undefined categories safely
      const categories = data.categories && Array.isArray(data.categories)
        ? data.categories.map((item: CategoryMapping) => item.category).filter(Boolean) || []
        : [];
      return {
        ...data,
        categories
      };
    }
    
    return data;
  } catch (error) {
    //console.error('Error in getPublicBlogBySlug:', error);
    throw error;
  }
};

// Public Blog Category Endpoints
export const getPublicCategories = async () => {
  try {
    //console.log('Fetching public categories with Supabase public client');
    
    // Query for all categories
    const { data, error } = await publicSupabase
      .from('blog_categories')
      .select('*')
      .order('name');
    
    if (error) {
      //console.error('Error fetching categories:', error);
      throw error;
    }
    
    return data || [];
  } catch (error) {
    //console.error('Error in getPublicCategories:', error);
    throw error;
  }
}; 